import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ShoppingBag, ExternalLink, ChevronLeft, ChevronRight, Sparkles, X, Heart } from 'lucide-react';

const SQUARE_CHECKOUT_URL = 'https://hrnestllc.square.site';

// Actual products from hrnestllc.square.site with real images and descriptions
// Purple versions shown first
const products = [
  {
    id: 1,
    name: 'Mug',
    price: '$19.99',
    images: [
      'https://154960456.cdn6.editmysite.com/uploads/1/5/4/9/154960456/M23RA3UFNY3AMRBQ2ANG3SE7.jpeg?width=800&optimize=medium'
    ],
    description: 'Start your morning with HR Nest energy! This branded ceramic mug is perfect for your coffee, tea, or favorite beverage. A great addition to your desk or home office.',
    colors: ['Purple'],
    fit: 'One Size',
    checkoutUrl: 'https://hrnestllc.square.site/product/mug/UJDCLA7BL5MWWCUGO74ZNU7L?cp=true&sa=true&sbp=false&q=false'
  },
  {
    id: 2,
    name: 'Tote Bag',
    price: '$14.99',
    images: [
      'https://154960456.cdn6.editmysite.com/uploads/1/5/4/9/154960456/X2DXYYUQ75LMWGWXDO6LSTYW.jpeg?width=800&optimize=medium'
    ],
    description: 'Stylish HR Nest branded tote bag - perfect for work, shopping, or everyday use. Show your HR Nest pride wherever you go!',
    colors: ['Purple'],
    fit: 'One Size',
    checkoutUrl: 'https://hrnestllc.square.site/product/tote-bag/B5ORWPTZHIHED5N4CP25M7DF?cs=true&cst=custom'
  },
  {
    id: 3,
    name: 'Sweatshirt',
    price: '$39.99',
    images: [
      'https://customer-assets.emergentagent.com/job_mindful-coaching-10/artifacts/5ov436g6_HRZVKFT2COHRB5TW5CBLOEOL.webp',
      'https://154960456.cdn6.editmysite.com/uploads/1/5/4/9/154960456/G64K33SWS4IICUFOAGRZXMSX.jpeg?width=800&optimize=medium',
      'https://154960456.cdn6.editmysite.com/uploads/1/5/4/9/154960456/HRZVKFT2COHRB5TW5CBLOEOL.jpeg?width=800&optimize=medium'
    ],
    description: 'Cozy, classic, and effortlessly empowering. This HR Nest sweatshirt features a soft, comfortable feel with the HR Nest logo displayed cleanly on the front for a timeless, everyday look. Designed for warmth without bulk, perfect for layering, working from home, or attending events.',
    colors: ['Purple', 'White', 'Blue'],
    fit: 'Unisex, relaxed fit',
    checkoutUrl: 'https://hrnestllc.square.site/product/sweatshirt-one-size/XU3PCT37OG6CXZVYTWRGEEP5?cs=true&cst=custom'
  },
  {
    id: 4,
    name: 'Hat',
    price: '$19.99',
    images: [
      'https://customer-assets.emergentagent.com/job_mindful-coaching-10/artifacts/bdbukosa_HK2464XG6P3VCKVJKUDUZKQX.webp',
      'https://154960456.cdn6.editmysite.com/uploads/1/5/4/9/154960456/NWXFDVAYGVWG3FHGBCNSWKGV.jpeg?width=800&optimize=medium',
      'https://154960456.cdn6.editmysite.com/uploads/1/5/4/9/154960456/HK2464XG6P3VCKVJKUDUZKQX.jpeg?width=800&optimize=medium'
    ],
    description: 'Sleek, versatile, and effortlessly on-brand. This HR Nest hat features a clean, structured design with the HR Nest logo displayed front and center. Made for comfort and durability, perfect for casual wear, events, or repping HR Nest on the go.',
    colors: ['Purple', 'White', 'Blue'],
    fit: 'Adjustable, unisex',
    checkoutUrl: 'https://hrnestllc.square.site/product/hat-one-size/7B2DVPSRD2F53GE4PUSDH2UJ?cs=true&cst=custom'
  },
  {
    id: 5,
    name: 'T-Shirt',
    price: '$24.99',
    images: [
      'https://customer-assets.emergentagent.com/job_mindful-coaching-10/artifacts/vvxninhz_JXLOROED5FWG2ABHKFPAX4DB.webp',
      'https://154960456.cdn6.editmysite.com/uploads/1/5/4/9/154960456/26RLI3XIYNW2PW6RGLJVHBII.jpeg?width=800&optimize=medium',
      'https://154960456.cdn6.editmysite.com/uploads/1/5/4/9/154960456/JXLOROED5FWG2ABHKFPAX4DB.jpeg?width=800&optimize=medium'
    ],
    description: 'This HR Nest t-shirt features a crisp design with the HR Nest logo front and center, giving it a polished yet casual look. The soft, breathable fabric makes it perfect for everyday wear, workshops, or styling up for a relaxed, professional vibe.',
    colors: ['Purple', 'White', 'Blue'],
    fit: 'Classic unisex tee',
    checkoutUrl: 'https://hrnestllc.square.site/product/t-shirt-one-size/RQVIULPJ2GAZC7NWDBFXXXSM?cs=true&cst=custom'
  }
];

// Product Detail Modal Component
const ProductModal = ({ product, isOpen, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!isOpen || !product) return null;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="relative bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300"
        >
          <X className="h-5 w-5 text-hr-text-primary" />
        </button>

        <div className="grid md:grid-cols-2 gap-0">
          {/* Image Section */}
          <div className="relative bg-gradient-to-br from-hr-purple-light to-hr-teal-light">
            <div className="aspect-square relative overflow-hidden">
              <img 
                src={product.images[currentImageIndex]} 
                alt={product.name}
                className="w-full h-full object-cover"
              />
              
              {/* Image Navigation */}
              {product.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300"
                  >
                    <ChevronLeft className="h-5 w-5 text-hr-purple" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300"
                  >
                    <ChevronRight className="h-5 w-5 text-hr-purple" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Navigation */}
            {product.images.length > 1 && (
              <div className="flex justify-center gap-2 p-4 bg-white/50">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                      idx === currentImageIndex ? 'border-hr-purple scale-110' : 'border-transparent opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details Section */}
          <div className="p-8 flex flex-col overflow-y-auto max-h-[500px]">
            <div className="mb-6">
              <h2 className="font-heading text-3xl font-bold text-hr-text-primary mb-2">{product.name}</h2>
              <p className="text-3xl font-bold text-hr-purple">{product.price}</p>
            </div>

            <p className="text-hr-text-secondary mb-6 leading-relaxed">{product.description}</p>

            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-hr-text-primary">Available Colors:</span>
                <div className="flex gap-2">
                  {product.colors.map((color, idx) => (
                    <span key={idx} className="px-3 py-1 rounded-full bg-hr-purple-light text-hr-purple text-sm font-medium">
                      {color}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-hr-text-primary">Fit:</span>
                <span className="text-hr-text-secondary">{product.fit}</span>
              </div>
            </div>

            <div className="mt-auto space-y-4">
              <a 
                href={product.checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full group bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                  <ShoppingBag className="mr-2 h-5 w-5" />
                  Checkout on Square
                  <ExternalLink className="ml-2 h-4 w-4 group-hover:rotate-12 transition-transform" />
                </Button>
              </a>
              <p className="text-center text-sm text-hr-text-muted">
                Secure checkout powered by Square
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product, onViewDetails }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card 
      className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 overflow-hidden cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onViewDetails(product)}
      data-testid={`product-${product.id}`}
    >
      <div className="relative h-72 overflow-hidden bg-gradient-to-br from-hr-purple-light to-hr-teal-light">
        <img 
          src={product.images[0]} 
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        
        {/* Price Badge */}
        <div className="absolute top-4 right-4 px-4 py-2 rounded-full bg-white/95 shadow-lg">
          <span className="font-bold text-hr-purple text-lg">{product.price}</span>
        </div>

        {/* Quick View Overlay */}
        <div className={`absolute inset-0 bg-hr-purple/80 flex items-center justify-center transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <span className="text-white font-semibold text-lg px-6 py-3 rounded-full border-2 border-white">
            View Details
          </span>
        </div>

        {/* Color Options */}
        {product.colors.length > 1 && (
          <div className="absolute bottom-4 left-4 flex gap-1">
            {product.colors.slice(0, 3).map((color, idx) => (
              <span key={idx} className="w-6 h-6 rounded-full bg-white/90 shadow-md flex items-center justify-center text-xs font-bold text-hr-purple">
                {color.charAt(0)}
              </span>
            ))}
          </div>
        )}
      </div>
      
      <CardContent className="p-6">
        <h3 className="font-heading text-xl font-semibold text-hr-text-primary mb-2 group-hover:text-hr-purple transition-colors duration-300">
          {product.name}
        </h3>
        <p className="text-sm text-hr-text-secondary line-clamp-2 mb-4">
          {product.description}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-hr-text-muted">{product.fit}</span>
          <Button 
            variant="outline" 
            size="sm"
            className="rounded-full border-hr-purple text-hr-purple hover:bg-hr-purple hover:text-white transition-all duration-300"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(product);
            }}
          >
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const Shop = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-hr-warm-bg via-white to-hr-teal-light/20 relative" data-testid="shop-hero">
        <div className="absolute top-0 right-0 w-96 h-96 bg-hr-teal/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-hr-purple/10 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-1/2 right-1/4 w-40 h-40 bg-hr-teal/5 rounded-full blur-2xl animate-blob" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-teal/10 mb-6 animate-bounce-subtle">
              <ShoppingBag className="h-4 w-4 text-hr-teal animate-pulse" />
              <span className="text-hr-teal text-sm font-medium">Official Store</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-hr-text-primary animate-fade-in-up">
              HR Nest <span className="text-gradient bg-gradient-to-r from-hr-teal to-hr-purple bg-clip-text text-transparent animate-gradient">Shop</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Support your journey with HR Nest merchandise. Show your pride and represent the community!
            </p>
          </div>
        </div>
      </section>

      {/* Giving Back Section */}
      <section className="py-16 bg-gradient-to-br from-hr-warm-bg to-white relative overflow-hidden" data-testid="shop-giving-back">
        <div className="absolute top-1/2 left-0 w-40 h-40 bg-hr-purple/5 rounded-full blur-2xl -translate-y-1/2 animate-float" />
        <div className="absolute top-1/2 right-0 w-40 h-40 bg-hr-teal/5 rounded-full blur-2xl -translate-y-1/2 animate-float-slow" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-hr-purple/10 text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-hr-purple to-hr-teal flex items-center justify-center mb-6">
              <Heart className="h-8 w-8 text-white" />
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-hr-text-primary mb-4">
              Shop with Purpose
            </h3>
            <p className="text-lg text-hr-text-secondary leading-relaxed max-w-2xl mx-auto">
              When you shop with HR Nest, you are giving back. A portion of all proceeds will be donated to local schools and charitable organizations that uplift students, families, and underserved communities.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid Section */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="shop-products">
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-hr-teal/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow" />
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-hr-purple/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow stagger-2" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-purple/10 mb-4">
              <Sparkles className="h-4 w-4 text-hr-purple animate-pulse" />
              <span className="text-hr-purple text-sm font-medium">Our Collection</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-hr-text-primary animate-fade-in-up">
              HR Nest Merchandise
            </h2>
            <p className="mt-4 text-lg text-hr-text-secondary animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Browse our collection and click to view details. Ready to buy? Checkout securely on Square.
            </p>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className="animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
              >
                <ProductCard product={product} onViewDetails={handleViewDetails} />
              </div>
            ))}
          </div>

          {/* Visit Full Shop CTA */}
          <div className="text-center mt-16 animate-fade-in-up stagger-4" style={{ opacity: 0 }}>
            <a 
              href={SQUARE_CHECKOUT_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="shop-now-btn"
            >
              <Button className="group bg-hr-teal hover:bg-hr-teal-dark text-white rounded-full px-10 py-7 text-lg shadow-2xl hover:shadow-hr-teal/40 transition-all duration-500 hover:-translate-y-2 hover:scale-105 btn-3d">
                <ShoppingBag className="mr-2 h-5 w-5 group-hover:animate-bounce" />
                Visit Full Shop on Square
                <ExternalLink className="ml-2 h-5 w-5 group-hover:rotate-12 transition-transform" />
              </Button>
            </a>
            <p className="mt-4 text-sm text-hr-text-muted">
              Secure checkout powered by Square
            </p>
          </div>
        </div>
      </section>

      {/* Product Detail Modal */}
      <ProductModal 
        product={selectedProduct} 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
      />
    </div>
  );
};

export default Shop;
